import {
  calculateSpreadPercentage,
  getSpreadPercentageInfo,
} from '@ui/features/Spread/useSpreadPercentage';
import { useFromInput } from '@ui/features/Trade/hooks/useFromInput';
import { useLeverage } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { PossibleBigNumber } from '@ui/types/utility';
import { getLiqPrice } from '@ui/utils/trade/getLiqPrice';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { useIsLong } from '../store/useIsLong';
import { useClosePositionFee } from './useClosePositionFee';
import { useCurrentLeverageMargin } from './useCurrentLeverageMargin';
import { usePositionSize } from './usePositionSize';

export const useLiqPrice = ({
  entryPrice,
  payTokenPrice,
}: {
  entryPrice: PossibleBigNumber;
  payTokenPrice: PossibleBigNumber;
}) => {
  // displaying on open Position flow
  const inputUsd = useFromInput((s) => s.input).value.usd;
  const isLong = useIsLong((s) => s._persistValue);
  const positionSizeBeforeFee = usePositionSize({
    inputUsd,
    isBeforeFee: true,
  });
  const positionSize = usePositionSize({ inputUsd, isBeforeFee: false });

  const leverage = useLeverage();
  const targetLeverageMargin = useCurrentLeverageMargin();
  const tradePair = useTradingPair((s) => s.useTradingPair());

  const closePositionFee = useClosePositionFee();

  return useMemo(() => {
    if (
      isNil(entryPrice) ||
      isNil(positionSize) ||
      isNil(targetLeverageMargin) ||
      isNil(closePositionFee) ||
      isNil(payTokenPrice) ||
      isNil(positionSizeBeforeFee) ||
      isNil(tradePair)
    )
      return;

    const positionQty = positionSizeBeforeFee.div(entryPrice); // for qty, since pass in SC with before fee qty, to align
    const liqLostP = targetLeverageMargin.liqLostP;
    const countableCollateralUsd = positionSize.div(leverage); // countable collateral usd is after open position fee

    const spreadPercentageInfo = getSpreadPercentageInfo({
      tradePair,
      positionSize: positionSize,
      pairPrice: entryPrice,
    });

    const slippageP = calculateSpreadPercentage({
      isLong: isLong,
      isClosePrice: true,
      spreadPercentageInfo,
    });

    return getLiqPrice({
      entryPrice,
      positionQty: positionQty,
      isLong,
      liqLostP: liqLostP,
      countableCollateralUsd,
      slippageP,
      // for open position flow, the countableFeesUsd is 0 due to do not have funding fee and holding fee; for existing position, it will need to included funding fee + holding fee + close fee
      closeFee: closePositionFee,
      fundingFee: ZERO_BIG_NUMBER,
      holdingFee: ZERO_BIG_NUMBER,
    });
  }, [
    closePositionFee,
    entryPrice,
    isLong,
    leverage,
    payTokenPrice,
    positionSize,
    positionSizeBeforeFee,
    targetLeverageMargin,
    tradePair,
  ]);
};
